import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Info } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import EsimAPI from 'api/faber/EsimAPI'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import SimOpsAPI from 'api/ops/v2/SimOpsAPI'
import ColorBox from 'common/Box/ColorWhiteBox'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import NormalText from 'common/Typography/NormalText'
import { mapStatesToFrontendDropdown } from 'helpers/backendStatesMapping'
import OrbitSession from 'hooks/useCookies'
import useModal from 'hooks/useModal'
import {
  ActionMenuForOneUser,
  RefreshNetworkDialog
} from 'pages/Customers/sharedComponents'
import CreateDeleteRequestDialog from 'pages/Customers/sharedComponents/CreateDeleteRequestDialog'
import SwitchWhitelistDialog from 'pages/Customers/sharedComponents/SwitchWhitelistDialog'
import UndoDeleteRequestDialog from 'pages/Customers/sharedComponents/UndoDeleteRequestDialog'
import { EsimRequestV2Dialog } from 'pages/Customers/sharedDialogs'
import AddUserPlanDialog from 'pages/Customers/sharedDialogs/AddUserPlanDialog'
import AssignVoucherDialog from 'pages/Customers/sharedDialogs/AssignVoucherDialog'
import CustomerWinbackDialog from 'pages/Customers/sharedDialogs/CustomerWinbackDialog'
import DeletePaymentMethodDialog from 'pages/Customers/sharedDialogs/DeletePaymentMethodDialog'
import EsimAllocateByWhitelistIDDialog from 'pages/Customers/sharedDialogs/EsimAllocateByWhitelistID'
import EditUserDetailsDialog from 'pages/OTPacks/SG/dialogs/EditUserDetails'
import { SubscriptionDetails } from 'types/api/faber/dataUsage.type'
import { TransformedFaberUserData } from 'types/api/faber/globalCustomers.type'
import {
  GetActiveUserPlansDetailsType,
  GetAllFaberUsersDetails,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType,
  UserPlanWithPrice
} from 'types/api/faber/oneTimePack.type'

import AllocateEsimDialog from '../dialogs/AllocateEsimDialog'
import EditEmailDialog from '../dialogs/EditEmailDialog'
import OTPDialog from '../dialogs/OTPDialog'

import CustomerPlansCard from './CustomerPlansCardV2'
import DataRemainingCardV2 from './DataRemainingCardV2'
import EsimStatusCardV3 from './EsimStatusCardV3'
import JetpacSubscriptionCard from './JetpacSubscriptionCard'
import JetpassVoucherTable from './JetpassVoucherTable'
import { ListSection } from './listUi'
import NetworkCard from './NetworkCard'
import PacksAllocationCard from './PacksAllocationCard'
import PromocodesDisplayForOneUser from './PromocodesDisplayForOneUser'
import SubHistoryV2 from './SubHistoryV2'
import SubscriptionTable from './SubscriptionTable'
import TerminatedCustomerPlans from './TerminatedCustomerPlans'
import VoicePacksCard from './VoicePackCard'

interface UserProfileProps {
  customerRow: TransformedFaberUserData
}

const normalizeUserPackPlan = (
  plan:
    | UserPlanWithPrice
    | GetActiveUserPlansDetailsType
    | GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
): UserPlanWithPrice => ({
  userPlanId: plan.userPlanId,
  createdAt: plan.createdAt,
  updatedAt: 'updatedAt' in plan ? plan.updatedAt : plan.createdAt,
  userId: plan.userId,
  iccid: plan.iccid,
  vendor: plan.vendor,
  catalogId: plan.catalogId,
  appliedPlanId: plan.appliedPlanId,
  planStatus: plan.planStatus,
  paymentIntent: 'paymentIntent' in plan ? plan.paymentIntent : '',
  packageName: plan.packageName,
  packageType: 'packageType' in plan ? plan.packageType : '',
  price: plan.price,
  supportedCountries: plan.supportedCountries,
  packageDataAllowed: plan.packageDataAllowed,
  packageValidityInDays: plan.packageValidityInDays,
  dataUsageRemainingInBytes:
    'dataUsageRemainingInBytes' in plan
      ? plan.dataUsageRemainingInBytes
      : plan.packageDataAllowed * 1073741824,
  packageExpiry: 'packageExpiry' in plan ? plan.packageExpiry : 0,
  allocatedFrom: plan.allocatedFrom,
  allocatedFromID: plan.allocatedFromID,
  packAssignmentId:
    'packAssignmentId' in plan ? plan.packAssignmentId : undefined
})

export default function UserProfileV3({ customerRow }: UserProfileProps) {
  const faberToken = OrbitSession.get('faberToken')

  const location = useLocation()

  // all dialogs
  const [isRefreshNetworkDialogOpen, setIsRefreshNetworkDialogOpen] =
    useState(false)
  const [isUndoDeleteRequestDialogOpen, setIsUndoDeleteRequestDialogOpen] =
    useState(false)
  const [isCreateDeleteRequestDialogOpen, setIsDeleteRequestDialogOpen] =
    useState(false)

  const [isSwitchWhitelistDialogOpen, setIsSwitchWhitelistDialogOpen] =
    useState(false)

  const [isEsimEmailDialogOpen, setIsEsimEmailDialogOpen] = useState(false)
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false)

  const [
    isEsimAllocateByWhitelistIDDialogOpen,
    setIsEsimAllocateByWhitelistIDDialogOpen
  ] = useState(false)

  /// OTP, EditEmail dialogs
  const [isOTPDialogOpen, setIsOTPDialogOpen] = useState(false)
  const [isEditEmailDialogOpen, setIsEditEmailDialogOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [otps, setOtps] = useState<{
    currentOtp: string
    newOtp: string
  } | null>(null)
  const [displayedEmail, setDisplayedEmail] = useState(customerRow.userEmail)

  // user plan dialog
  const [isUserPlanDialogOpen, setIsUserPlanDialogOpen] = useState(false)

  const { data: deletionRequest } = useQuery(
    ['delete-request', customerRow.userID],
    async () => {
      return await EsimAPI.getDeleteRequest({
        userId: customerRow.userID,
        faberToken
      })
    }
  )

  // assign voucher
  const [isAssignVoucherDialogOpen, setIsAssignVoucherDialogOpen] =
    useState(false)

  // customer winback
  const [isCustomerWinbackDialogOpen, setIsCustomerWinbackDialogOpen] =
    useState(false)

  // delete user payment method dialog
  const [isDeletePaymentMethodDialogOpen, setIsDeletePaymentMethodDialogOpen] =
    useState(false)

  //CX USER PLAN
  const [userPacksUserPlans, setUserPacksUserPlans] = useState<
    UserPlanWithPrice[]
  >([])

  const pageSize = 10
  const [page, setPage] = useState(0)
  const [pageUserPacks, setPageUserPacks] = useState(0)
  const [packsTotal, setPacksTotal] = useState(0)

  //DATA REAMINING CARD
  const [dataRemaining, setDataRemaining] = useState(0)
  const [otpackDataRemaining, setOtpackDataRemaining] = useState(0)

  const [subscriptionData, setSubscriptionData] = useState<
    SubscriptionDetails[]
  >([])

  const [whitelistId, setWhitelistId] = useState('')

  //UPDATE EMAIL
  const updateEmailMutation = useMutation<void, Error, string>(
    async (newEmail: string) => {
      const response = await fetch(
        `https://ssg-orbit-sim.circleslife.co/v1/labs/sim/ops/account/user/email/update/${customerRow.userID}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${faberToken}`
          },
          body: JSON.stringify({ newEmail: newEmail })
        }
      )

      if (!response.ok) {
        throw new Error('Error updating email')
      }

      return response.json()
    },
    {
      onSuccess: () => {
        toast.success('Email updated successfully')
      },
      onError: () => {
        toast.error('Error updating email')
      }
    }
  )

  //GET ALL ACTIVE OTPACK USER PLANS API
  const {
    refetch: refetcheOtpackUserPlans,
    isFetching: isFetchingOTPackUserPlans
  } = useQuery(
    ['getAllActiveInactiveOtpackUserPlans', customerRow.userID, pageUserPacks],
    async () => {
      return OneTimePackApi.getAllActiveInactiveOtpackUserPlans(
        customerRow.userID,
        faberToken,
        pageSize,
        pageUserPacks * pageSize
      )
    },
    {
      onSuccess: (res) => {
        const allPlans = (res?.data?.body?.data || []).map(normalizeUserPackPlan)
        const visiblePlans = allPlans.filter((plan) => {
          const status = String(plan.planStatus || '').toUpperCase()
          return status === 'ACTIVE' || status === 'INACTIVE'
        })

        const getStatusPriority = (status: string) => {
          const normalized = String(status || '').toUpperCase()
          if (normalized === 'ACTIVE') return 0
          if (normalized === 'INACTIVE') return 1
          return 2
        }

        const sortedPlans = [...visiblePlans].sort((a, b) => {
          const statusOrder =
            getStatusPriority(a.planStatus) - getStatusPriority(b.planStatus)
          if (statusOrder !== 0) return statusOrder

          return (
            new Date(String(b.createdAt)).getTime() -
            new Date(String(a.createdAt)).getTime()
          )
        })

        setUserPacksUserPlans(sortedPlans)
        setPacksTotal(sortedPlans.length)
        return res.data
      },
      onError: () => {
        toast.error('Failed to fetch active/inactive packs')
        return []
      },
      enabled: !!customerRow.userID
    }
  )

  // subscriptionData
  const {
    refetch: refetchSubscriptionData,
    isFetching: isFetchingSubscriptionData
  } = useQuery(
    ['userSubscriptionData', customerRow.userID],
    async () => {
      return DataApi.getUserSubscription(faberToken, customerRow.userID)
    },
    {
      onSuccess: (res) => {
        setSubscriptionData(res.data.data)
      }
    }
  )

  //eSIM STATUS API (gets whitelist)
  const { refetch: refetchEsimStatus } = useQuery(
    ['get-esim-status', customerRow.userID],
    async () => {
      const { data } = await SimOpsAPI.getUserEsimLiveStatus(customerRow.userID)
      return data
    },
    {
      onSuccess: (res) => {
        if (!res.data) {
          setWhitelistId('')
        } else {
          setWhitelistId(res.data?.whitelist)
        }
        return res.data
      },
      enabled: !!customerRow.userID
    }
  )

  const {
    data: esimsInfoPaginated,
    isLoading: isEsimsInfoPaginatedLoading,
    refetch: refetchEsimsInfoPaginated
  } = useQuery(
    ['get-esim-by-userID-paginated', customerRow.userID, page],
    async () => {
      const { data } = await SimOpsAPI.getUserEsims({
        userId: customerRow.userID,
        limit: pageSize,
        offset: page * pageSize
      })
      return data
    },
    {
      enabled: !!customerRow.userID,
      keepPreviousData: true
    }
  )

  const {
    data: esimsInfoStatus,
    isLoading: isEsimsInfoStatusLoading,
    refetch: refetchEsimsInfoStatus
  } = useQuery(
    ['get-esim-by-userID', customerRow.userID],
    async () => {
      const { data } = await SimOpsAPI.getUserEsimSummary(customerRow.userID)
      return data
    },
    {
      enabled: !!customerRow.userID
    }
  )

  //DATA REMAINING CARD API FOR SUBSCRIPTION USERS
  const { data: subdata, isFetching: isSubDataLoading } = useQuery(
    ['getOverviewCRDData', esimsInfoStatus?.data?.enabledEsimICCID],
    async () => {
      const getCDROverviewDataResponse = await DataApi.getDataCDROverview(
        esimsInfoStatus?.data?.enabledEsimICCID ?? '',
        faberToken
      )
      return getCDROverviewDataResponse.data
    },
    {
      onSuccess: (res) => {
        return res
      },
      onError: () => {
        return {}
      },
      //TODO: CHECK IF NEED && SUBSCRIPTION
      enabled:
        !!esimsInfoStatus?.data?.enabledEsimICCID && customerRow.subscription
    }
  )

  useEffect(() => {
    if (subdata && customerRow.subscription) {
      setDataRemaining(subdata.totalDataAllocatedMB - subdata.totalDataUsedMB)
    } else {
      setDataRemaining(0)
    }
  }, [subdata])

  /* FUNCTION CALLS */
  const allFields = useMemo(() => {
    if (!customerRow.firstName && !customerRow.lastName) {
      return [
        {
          mainFieldTitle: 'Profile',
          fields: [
            {
              fieldName: 'Name',
              fieldValue: customerRow.fullName
            },
            {
              fieldName: 'Email',
              fieldValue: customerRow.userEmail
            }
          ]
        }
      ]
    }
    return [
      {
        mainFieldTitle: 'Profile',
        fields: [
          {
            fieldName: 'First Name',
            fieldValue: customerRow.firstName
          },
          {
            fieldName: 'Last Name',
            fieldValue: customerRow.lastName
          },
          {
            fieldName: 'Email',
            fieldValue: customerRow.userEmail
          }
        ]
      }
    ]
  }, [customerRow])

  // EVENT HANDLER FUNCTIONS

  const {
    openModal: openReprovisionModal,
    closeModal: closeReprovisionModal,
    isShowing: isReprovisionModalOpen
  } = useModal()

  const handleReprovisionEsimOpen = () => {
    openReprovisionModal()
  }

  const handleReprovisionEsimClose = () => {
    closeReprovisionModal()
  }

  const handleRefreshNetworkDialogOpen = () => {
    setIsRefreshNetworkDialogOpen(true)
  }
  const handleUndoDeleteRequestDialogOpen = () => {
    setIsUndoDeleteRequestDialogOpen(true)
  }
  const handleCreateDeleteRequestDialogOpen = () => {
    setIsDeleteRequestDialogOpen(true)
  }
  const handleRefreshNetworkDialogClose = () => {
    setIsRefreshNetworkDialogOpen(false)
  }
  const handleUndoDeleteRequestDialogClose = () => {
    setIsUndoDeleteRequestDialogOpen(false)
  }
  const handleCreateDeleteRequestDialogClose = () => {
    setIsDeleteRequestDialogOpen(false)
  }

  const handleEsimDialogOpen = () => {
    setIsEsimEmailDialogOpen(true)
  }

  const handleEsimDialogClose = () => {
    setIsEsimEmailDialogOpen(false)
  }

  const handleUserPlanDialogOpen = () => {
    setIsUserPlanDialogOpen(true)
  }
  const handleUserPlanDialogClose = () => {
    setIsUserPlanDialogOpen(false)
  }

  const handleAssignVoucherDialogOpen = () => {
    setIsAssignVoucherDialogOpen(true)
  }

  const handleAssignVoucherDialogClose = () => {
    setIsAssignVoucherDialogOpen(false)
  }

  const handleCustomerWinbackDialogOpen = () => {
    setIsCustomerWinbackDialogOpen(true)
  }

  const handleCustomerWinbackDialogClose = () => {
    setIsCustomerWinbackDialogOpen(false)
  }

  const handleEditUserDialogOpen = () => {
    setIsEditUserDialogOpen(true)
  }

  const handleEditUserDialogClose = () => {
    setIsEditUserDialogOpen(false)
  }

  const handleSwitchWhitelistDialogOpen = () => {
    setIsSwitchWhitelistDialogOpen(true)
  }
  const handleSwitchWhitelistDialogClose = () => {
    setIsSwitchWhitelistDialogOpen(false)
  }

  const handleDeletePaymentMethodDialogOpen = () => {
    setIsDeletePaymentMethodDialogOpen(true)
  }

  const handleDeletePaymentMethodDialogClose = () => {
    setIsDeletePaymentMethodDialogOpen(false)
  }

  // OTP dialog handlers
  const handleOTPDialogClose = () => {
    setIsOTPDialogOpen(false)
  }

  const handleOTPDialogApprove = () => {
    setIsOTPDialogOpen(false)
    updateEmailMutation.mutate(newEmail, {
      onSuccess: () => {
        setDisplayedEmail(newEmail) // Update the displayed email
      },
      onError: () => {
        toast.error('Error updating email')
      }
    })
  }

  // edit email dialog handlers
  // const handleEditEmailDialogOpen = () => {
  //   setIsEditEmailDialogOpen(true)
  // }
  const handleEditEmailDialogClose = () => {
    setIsEditEmailDialogOpen(false)
  }

  const handleEditEmailDialogToOTPDialog = (email, otpData) => {
    setNewEmail(email)
    setOtps(otpData)
    setIsEditEmailDialogOpen(false)
    setIsOTPDialogOpen(true)
  }

  useEffect(() => {
    refetcheOtpackUserPlans()
    refetchEsimStatus()
    refetchEsimsInfoStatus()
    refetchEsimsInfoPaginated()
    refetchSubscriptionData()
  }, [location, customerRow, pageUserPacks])

  const updateUserDetails = () => {
    refetchEsimsInfoPaginated()
    refetchEsimsInfoStatus()
    refetchEsimStatus()
  }

  const transformedRowInformation = useMemo(() => {
    const newCustomerRow: TransformedFaberAndGlobalUsersType = {
      ...customerRow,
      iccid: esimsInfoStatus?.data?.enabledEsimICCID ?? ''
    }
    return newCustomerRow
  }, [customerRow, esimsInfoStatus?.data?.enabledEsimICCID ?? ''])

  const hasPendingDeletionRequest =
    deletionRequest?.data?.data?.status === 'PENDING_DELETION'

  return (
    <>
      {/* ALL DIALOGS FOR ACTIONS HERE */}
      <EditEmailDialog
        open={isEditEmailDialogOpen}
        onClose={handleEditEmailDialogClose}
        onSave={handleEditEmailDialogToOTPDialog}
        currentEmail={customerRow.userEmail || ''}
      />

      <OTPDialog
        open={isOTPDialogOpen}
        onClose={handleOTPDialogClose}
        onApprove={() => {
          setDisplayedEmail(newEmail)
          handleOTPDialogApprove()
        }}
        currentEmail={displayedEmail || ''}
        newEmail={newEmail}
        otps={otps || { currentOtp: '', newOtp: '' }}
      />

      <EditUserDetailsDialog
        userId={customerRow.userID}
        handleEditUserDialogClose={handleEditUserDialogClose}
        editUserDetailsDialogOpen={isEditUserDialogOpen}
        productType='otp'
      />

      <RefreshNetworkDialog
        faberToken={faberToken}
        handleRefreshNetworkDialogClose={handleRefreshNetworkDialogClose}
        refreshNetworkDialogOpen={isRefreshNetworkDialogOpen}
        customerRow={transformedRowInformation}
        isExternal={false} // internal ops user, not external user profile
      />

      <UndoDeleteRequestDialog
        faberToken={faberToken}
        handleDeleteRequestDialogClose={handleUndoDeleteRequestDialogClose}
        undoDeleteRequestDialogOpen={isUndoDeleteRequestDialogOpen}
        customerRow={transformedRowInformation}
      />

      <CreateDeleteRequestDialog
        faberToken={faberToken}
        handleCreateRequestDialogClose={handleCreateDeleteRequestDialogClose}
        createDeleteRequestDialogOpen={isCreateDeleteRequestDialogOpen}
        customerRow={transformedRowInformation}
      />

      <EsimRequestV2Dialog
        faberToken={faberToken}
        handleRequestEsimDialogClose={handleEsimDialogClose}
        requestEsimDialog={isEsimEmailDialogOpen}
        customerRow={transformedRowInformation}
        productType='otp'
        updateUserDetails={updateUserDetails}
        isExternal={false} // internal ops user, not external user profile
      />

      <AddUserPlanDialog
        userPlanDialogOpen={isUserPlanDialogOpen}
        userId={customerRow.userID}
        handleUserPlanDialogClose={handleUserPlanDialogClose}
      />

      <AssignVoucherDialog
        open={isAssignVoucherDialogOpen}
        userId={customerRow.userID}
        onClose={handleAssignVoucherDialogClose}
      />

      <CustomerWinbackDialog
        customerWinbackDialogOpen={isCustomerWinbackDialogOpen}
        userId={customerRow.userID}
        handleCustomerWinbackDialogClose={handleCustomerWinbackDialogClose}
      />

      <AllocateEsimDialog
        isDialogOpen={isReprovisionModalOpen}
        handleClose={handleReprovisionEsimClose}
        userId={customerRow.userID}
      />

      <SwitchWhitelistDialog
        userId={customerRow.userID}
        iccid={esimsInfoStatus?.data?.enabledEsimICCID ?? ''}
        esimWhitelist={whitelistId}
        isDialogOpen={isSwitchWhitelistDialogOpen}
        handleDialogClose={handleSwitchWhitelistDialogClose}
        isExternal={false} // internal ops user, not external user profile
      />

      <DeletePaymentMethodDialog
        deletePaymentMethodDialogOpen={isDeletePaymentMethodDialogOpen}
        handleDeletePaymentMethodDialogClose={
          handleDeletePaymentMethodDialogClose
        }
        userID={customerRow.userID}
        isExternal={false}
      />

      <EsimAllocateByWhitelistIDDialog
        isEsimAllocateByWhitelistIDDialogOpen={
          isEsimAllocateByWhitelistIDDialogOpen
        }
        handleCancelEsimAllocateByWhitelistIDDialog={() =>
          setIsEsimAllocateByWhitelistIDDialogOpen(false)
        }
        faberToken={faberToken}
        customerRow={transformedRowInformation}
      />

      <Stack spacing={3} mt={3}>
        <Stack>
          <NormalText isBold variant='h3'>
            Check the following steps to troubleshoot network issues:
          </NormalText>
          <NormalText>
            {' '}
            <em>
              Please get customers to take screenshots at each stage and send it
              as the screenshots will be needed if issue needs to be escalated
            </em>
          </NormalText>
        </Stack>
        <Grid container spacing={4}>
          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {isFetchingOTPackUserPlans && customerRow ? (
                <CircularProgress />
              ) : (
                <DataRemainingCardV2
                  dataRemaining={dataRemaining}
                  customerRow={customerRow}
                  otpackDataRemaining={otpackDataRemaining}
                />
              )}
            </Box>
          </Grid>

          <Grid item>
            {isEsimsInfoPaginatedLoading || isEsimsInfoStatusLoading ? (
              <CircularProgress />
            ) : (
              <EsimStatusCardV3
                userId={customerRow.userID}
                esimStatus={esimsInfoStatus?.data}
                esimsInfo={esimsInfoPaginated}
                pageNumber={page + 1}
                setPage={setPage}
                pageCount={Math.ceil(
                  (esimsInfoPaginated?.body?.total ?? 1) / pageSize
                )}
              />
            )}
          </Grid>

          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {isSubDataLoading && customerRow ? (
                <CircularProgress />
              ) : (
                <NetworkCard whitelist={whitelistId} />
              )}
            </Box>
          </Grid>

          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {isFetchingSubscriptionData ? (
                <CircularProgress />
              ) : (
                <JetpacSubscriptionCard subscriptionData={subscriptionData} />
              )}
            </Box>
          </Grid>
        </Grid>

        {hasPendingDeletionRequest && (
          <Stack
            direction='row'
            spacing={1}
            sx={{
              padding: 2,
              bgcolor: 'rgba(0, 0, 255, 0.05)',
              border: '1px solid rgba(0, 0, 255, 0.58)',
              borderRadius: 1,
              alignItems: 'center'
            }}
          >
            <Info sx={{ color: '#000869', fontSize: 20 }} />
            <Typography variant='body2' sx={{ color: '#000869' }}>
              This account has a pending deletion request. You can undo it if
              needed.
            </Typography>
          </Stack>
        )}

        <Grid item>
          <ColorBox>
            <Stack
              flexDirection={'row'}
              sx={{ justifyContent: 'flex-end', mb: 2 }}
            >
              <ActionMenuForOneUser
                productType='otp'
                handleEsimRequestDialogOpen={() => void 0}
                handleRefreshNetworkDialogOpen={handleRefreshNetworkDialogOpen}
                handleUndoDeleteRequest={handleUndoDeleteRequestDialogOpen}
                handleAssignVoucherDialogOpen={handleAssignVoucherDialogOpen}
                handleWinbackDialogOpen={handleCustomerWinbackDialogOpen}
                handleSuspendOrUnsuspendDialogOpen={() => void 0}
                showRefreshNetworkButton
                handleEsimRequestDialogV2Open={handleEsimDialogOpen}
                handleCancelSubDialogOpen={() => void 0}
                handleUserPlanDialogOpen={handleUserPlanDialogOpen}
                handleUserEditDetailsDialogOpen={handleEditUserDialogOpen}
                userId={customerRow.userID}
                handleSwitchWhitelistDialogOpen={
                  handleSwitchWhitelistDialogOpen
                }
                handleDeletePaymentMethodDialogOpen={
                  handleDeletePaymentMethodDialogOpen
                }
                handleReprovisionEsim={handleReprovisionEsimOpen}
                hasPendingDeletionRequest={hasPendingDeletionRequest}
                handleCreateDeleteRequest={handleCreateDeleteRequestDialogOpen}
                EsimAllocateByWhitelistIDDialogOpen={() =>
                  setIsEsimAllocateByWhitelistIDDialogOpen(true)
                }
              />
            </Stack>
            <Box sx={{ p: 2 }}>
              {allFields.map((field, index) => (
                // TO DO: THIS IS NOT READY HARDCODED BACKEND
                // edit email function
                <React.Fragment key={index + field.mainFieldTitle}>
                  {field.fields && field.fields.length > 0 && (
                    <ProfileListInfo
                      title={field.mainFieldTitle}
                      fields={field.fields.map((item) => {
                        if (
                          customerRow.userID.includes('email') &&
                          item.fieldName === 'Email'
                        ) {
                          return {
                            fieldName: item.fieldName,
                            fieldValue: (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <span>
                                  {item.fieldName === 'Email'
                                    ? displayedEmail
                                    : item.fieldValue}
                                </span>
                                {/* {item.fieldName === 'Email' && (
                                  <button
                                    style={{
                                      marginLeft: '8px',
                                      padding: '4px 8px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      backgroundColor: '#f0f0f0',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px'
                                    }}
                                    onClick={handleEditEmailDialogOpen}
                                  >
                                    Edit
                                  </button>
                                )} */}
                              </div>
                            )
                          }
                        }
                        return {
                          fieldName: item.fieldName,
                          fieldValue: item.fieldValue || ''
                        }
                      })}
                    >
                      {/* TO DO: WHEN EDIT USER IS UP FOR ONE TIME PACK */}
                      {/* <EditIconImg onEdit={handleUserDialogOpen} /> */}
                    </ProfileListInfo>
                  )}
                  {index === 0 && (
                    <>
                      <ProfileListInfo
                        proportion={4}
                        title=''
                        fields={[
                          {
                            fieldName: 'MSISDN',
                            fieldValue: customerRow.msisdn
                          },
                          {
                            fieldName: 'ICCID',
                            fieldValue:
                              esimsInfoStatus?.data?.enabledEsimICCID ?? ''
                          },

                          {
                            fieldName: 'Customer Id',
                            fieldValue: customerRow.userID
                          }
                        ]}
                      />
                    </>
                  )}
                  {index === 1 && (
                    <ProfileListInfo
                      proportion={4}
                      title=''
                      fields={[
                        {
                          fieldName: 'Account Status',
                          fieldValue: mapStatesToFrontendDropdown(
                            customerRow.planStatus ? customerRow.planStatus : ''
                          )
                        }
                      ]}
                    />
                  )}
                  {index !== allFields.length - 1 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </ColorBox>
        </Grid>

        <ColorBox>
          <ListSection
            title='JetPro / JetFlex Subscriptions'
            count={subscriptionData?.length ?? 0}
          >
            <SubscriptionTable
              subscriptionData={subscriptionData}
              userId={customerRow.userID}
              faberToken={faberToken}
            />
          </ListSection>
        </ColorBox>

        <ColorBox>
          {customerRow.subscription ? (
            <SubHistoryV2 />
          ) : (
            <CustomerPlansCard
              userPacksUserPlans={userPacksUserPlans}
              setOtpackDataRemaining={setOtpackDataRemaining}
              isExternal={false}
              userId={customerRow.userID}
              pageNumber={pageUserPacks + 1}
              setPage={setPageUserPacks}
              pageCount={Math.ceil(packsTotal / pageSize)}
              isFetchingOTPackUserPlans={isFetchingOTPackUserPlans}
            />
          )}
        </ColorBox>

        <ColorBox>
          <PacksAllocationCard userId={customerRow.userID} />
        </ColorBox>

        <ColorBox>
          <TerminatedCustomerPlans
            isExternal={false}
            userId={customerRow.userID}
          />
        </ColorBox>

        <ColorBox>
          <VoicePacksCard userId={customerRow.userID} />
        </ColorBox>

        <ColorBox>
          <ListSection title='Promocodes & Vouchers'>
            <PromocodesDisplayForOneUser userId={customerRow.userID} />
          </ListSection>
        </ColorBox>

        {/* Jetpass vouchers */}
        <ColorBox>
          <ListSection title='Jetpass Vouchers'>
            <JetpassVoucherTable
              userId={customerRow.userID}
              isExternal={false}
            />
          </ListSection>
        </ColorBox>
      </Stack>
    </>
  )
}

export interface TransformedFaberUsersType extends GetAllFaberUsersDetails {
  iccid: string
}

export interface TransformedFaberAndGlobalUsersType
  extends TransformedFaberUserData {
  iccid: string
}
